<template>
  <v-list nav class="px-0" overflow-y="auto">
    <template v-if="loading">
      <v-skeleton-loader
        type="list-item, subtitle, list-item-three-line, subtitle, list-item-two-line, divider, text"
      ></v-skeleton-loader>
    </template>

    <template v-else-if="userHasNoWorkspaces">
      <v-empty-state
        class="fill-height"
        title="No Workspaces"
        text='Start with "Top Accounts"?'
        color="primary"
        icon="mdi-view-dashboard-variant"
      >
        <create-workspace-dialog
          class="ml-2"
          key="create-workspace-dialog-bottom"
          @workspace-created="workspace => onWorkspaceCreated(workspace)"
        />
      </v-empty-state>
    </template>

    <template v-else>
      <v-text-field
        v-model="search"
        hide-details
        variant="outlined"
        :placeholder="$t('basicActions.search')"
        class="px-2 pt-4 pb-6"
      >
        <template #prepend-inner>
          <v-icon>mdi-magnify</v-icon>
        </template>
        <template #append>
          <create-workspace-dialog
            class="ml-2"
            key="create-workspace-dialog-bottom"
            @workspace-created="workspace => onWorkspaceCreated(workspace)"
          >
            <template #activator="props">
              <v-btn icon v-bind="props" variant="text" color="primary">
                <v-icon> mdi-plus-circle-outline </v-icon>
              </v-btn>
            </template>
          </create-workspace-dialog>
        </template>
      </v-text-field>

      <v-list v-for="(workspaces, category) in categorizedWorkspaces" :key="category" class="pb-0">
        <v-list-subheader v-if="workspaces.length > 0">
          {{ category.replaceAll('_', ' ') }}
        </v-list-subheader>
        <v-list-item
          :to="`/workspaces/${workspace.id}`"
          v-for="workspace in workspaces"
          :key="workspace.id"
          color="primary"
        >
          <v-list-item-title>
            {{ workspace.name }}
          </v-list-item-title>
          <template #append v-if="workspace.shared_users?.length > 0">
            <v-icon size="x-small"> mdi-account-multiple </v-icon>
          </template>
        </v-list-item>
      </v-list>
      <v-col>
        <v-divider></v-divider>
      </v-col>
      <v-col>
        <create-workspace-dialog
          class="ml-2"
          key="create-workspace-dialog-bottom"
          @workspace-created="workspace => onWorkspaceCreated(workspace)"
        />
      </v-col>
    </template>
  </v-list>
</template>

<script>
import CreateWorkspaceDialog from '@/components/workspaces/CreateWorkspaceDialog.vue'
import { useFirestoreWorkspaces } from '@/composables/workspaces/useFirestoreWorkspaces'
import { useAuthStore } from '@/store/auth'
import { useCommunicationStore } from '@/store/communication'
import { mapStores } from 'pinia'
import { log } from '@/plugins/logger'
import BasicTooltip from '@/components/tooltips/BasicTooltip.vue'

export default {
  name: 'WorkspaceNavigation',
  setup() {
    const {
      workspaces,
      loadingWorkspaces,
      getUserOwnedWorkspaces,
      sharedWorkspaces,
      getSharedWorkspaces
    } = useFirestoreWorkspaces()
    return {
      workspaces,
      loadingWorkspaces,
      getUserOwnedWorkspaces,
      sharedWorkspaces,
      getSharedWorkspaces
    }
  },
  components: {
    CreateWorkspaceDialog,
    BasicTooltip
  },
  data() {
    return {
      search: '',
      addGroupMenu: false,
      deleteGroupMenuOpen: false,
      revertingState: false,
      groupName: '',
      showWorkspaceNav: localStorage.getItem('showWorkspaceNav') === 'true',
      drag: false,
      expandedGroups: localStorage.getItem('expandedGroups')
        ? JSON.parse(localStorage.getItem('expandedGroups'))
        : [],
      localLoading: false // local loading watches everything and is set to false when everything is done. Important for proper rendering w vue reactivity system,
    }
  },
  computed: {
    ...mapStores(useAuthStore, useCommunicationStore),

    userHasNoWorkspaces() {
      return this.workspaces.length === 0 && this.sharedWorkspaces.length === 0
    },

    loading() {
      return this.loadingWorkspaces || this.localLoading
    },

    allWorkspaces() {
      return this.workspaces
        .concat(this.sharedWorkspaces)
        .filter(workspace => {
          return (
            workspace.name.toLowerCase().includes(this.search.toLowerCase()) || this.search === ''
          )
        })
        .sort((a, b) => a.name.localeCompare(b.name))
    },

    categorizedWorkspaces() {
      const returnWorkspaceCategories = {
        Personal: [],
        Shared_with_me: []
      }
      const isPersonalWorkspace = workspace => workspace.created_by === this.authStore.userData.uid

      this.allWorkspaces.forEach(workspace => {
        if (isPersonalWorkspace(workspace)) {
          returnWorkspaceCategories.Personal.push(workspace)
        } else {
          returnWorkspaceCategories.Shared_with_me.push(workspace)
        }
      })

      return returnWorkspaceCategories
    }
  },
  watch: {
    showWorkspaceNav(val) {
      if (val) {
        this.getUserOwnedWorkspaces()
        localStorage.setItem('showWorkspaceNav', 'true')
      } else {
        localStorage.setItem('showWorkspaceNav', 'false')
      }
    }
  },
  methods: {
    toggleGroup(groupId) {
      if (this.expandedGroups.includes(groupId)) {
        this.expandedGroups = this.expandedGroups.filter(id => id !== groupId)
        // set localStorage for persistence
        localStorage.setItem('expandedGroups', JSON.stringify(this.expandedGroups))
      } else {
        this.expandedGroups.push(groupId)
        // set localStorage for persistence
        localStorage.setItem('expandedGroups', JSON.stringify(this.expandedGroups))
      }
    },
    onWorkspaceCreated(workspace) {
      // add and auto open
      this.workspaces.push(workspace)
      this.$router.push(`/workspaces/${workspace.id}`)
    }
  },
  async mounted() {
    if (this.authStore.userData) {
      this.localLoading = true
      await this.authStore.refreshUserData()
      await this.getUserOwnedWorkspaces()
      await this.getSharedWorkspaces()
      this.localLoading = false
    }
  }
}
</script>

<style scoped>
.ghost {
  border-bottom: 2px solid #1e88e5; /* Shows only the bottom border */
  border-radius: 0; /* Keeps the corners sharp */
}
</style>
